import React from 'react';
import { Link } from 'react-router-dom';
import { 
  XCircle,
  AlertTriangle,
  Mail,
  ChevronRight,
  ArrowUpRight,
  Info
} from 'lucide-react';
import UserNavbar from '../components/UserNavbar';
import Footer from '../components/Footer';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-[#111111]">
      <UserNavbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">LEGAL</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 tracking-tight">
            Refund & Cancellation Policy
          </h1>
          <p className="text-white/60 text-lg max-w-2xl">
            Please read this policy carefully before making a purchase.
          </p>
          <p className="text-white/40 text-sm mt-4">
            Last updated: January 2026
          </p>
        </div>
      </section>

      {/* Main Policy Content */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-4xl mx-auto">
          
          {/* Important Notice - NO REFUNDS */}
          <div className="mb-16">
            <div className="border-2 border-red-500/50 bg-red-500/10 p-8 rounded-lg mb-8">
              <div className="flex items-start gap-4">
                <XCircle className="h-8 w-8 text-red-400 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-semibold text-white mb-4">No Refund Policy</h2>
                  <p className="text-white/80 text-lg leading-relaxed mb-4">
                    <strong>All purchases of NeoPass Learning credits are final and non-refundable.</strong>
                  </p>
                  <p className="text-white/60 leading-relaxed">
                    Due to the digital nature of our service and the immediate delivery of credits upon purchase, 
                    we do not offer refunds, returns, or cancellations under any circumstances once a transaction 
                    has been completed.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Why No Refunds */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <AlertTriangle className="h-7 w-7 text-accent" />
              <h2 className="text-3xl font-medium text-white">Why We Don't Offer Refunds</h2>
            </div>
            
            <div className="space-y-6">
              <div className="p-6 border border-white/10 bg-white/[0.02] rounded-lg">
                <h3 className="text-lg font-medium text-white mb-3">1. Instant Digital Delivery</h3>
                <p className="text-white/60 leading-relaxed">
                  Credits are delivered instantly to your account upon successful payment. Once credits are 
                  added, they become immediately available for use, making it impossible to "return" the product.
                </p>
              </div>
              
              <div className="p-6 border border-white/10 bg-white/[0.02] rounded-lg">
                <h3 className="text-lg font-medium text-white mb-3">2. Non-Transferable Service</h3>
                <p className="text-white/60 leading-relaxed">
                  Our AI-powered learning assistance is a consumable digital service. Each credit represents 
                  computational resources and API usage that cannot be recovered once allocated.
                </p>
              </div>
              
              <div className="p-6 border border-white/10 bg-white/[0.02] rounded-lg">
                <h3 className="text-lg font-medium text-white mb-3">3. Prevention of Abuse</h3>
                <p className="text-white/60 leading-relaxed">
                  A no-refund policy helps prevent misuse of our service where users could potentially use 
                  credits and then request refunds, which would be unfair to legitimate users.
                </p>
              </div>
            </div>
          </div>

          {/* Before You Purchase */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <Info className="h-7 w-7 text-accent" />
              <h2 className="text-3xl font-medium text-white">Before You Purchase</h2>
            </div>
            
            <div className="bg-accent\/5 border border-accent\/20 p-8 rounded-lg">
              <p className="text-white/80 mb-6 leading-relaxed">
                We strongly encourage you to:
              </p>
              <ul className="space-y-4 text-white/70">
                <li className="flex items-start gap-3">
                  <span className="text-accent font-bold">•</span>
                  <span>Review all features and pricing information before purchasing</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-accent font-bold">•</span>
                  <span>Start with a smaller credit pack to test if the service meets your needs</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-accent font-bold">•</span>
                  <span>Read our <Link to="/terms" className="text-accent hover\:text-accent-light underline">Terms of Service</Link> to understand the full scope of our service</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-accent font-bold">•</span>
                  <span>Contact us at <a href="mailto:neopassbusiness@gmail.com" className="text-accent hover\:text-accent-light underline">neopassbusiness@gmail.com</a> if you have any questions before purchasing</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Exceptions */}
          <div className="mb-16">
            <h2 className="text-3xl font-medium text-white mb-8">Exceptional Circumstances</h2>
            
            <div className="p-6 border border-white/10 bg-white/[0.02] rounded-lg">
              <p className="text-white/70 leading-relaxed mb-4">
                In rare cases, we may consider exceptions at our sole discretion for the following situations:
              </p>
              <ul className="space-y-3 text-white/60 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-white/40">•</span>
                  Duplicate charges due to technical errors (with proof of double payment)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-white/40">•</span>
                  Credits not delivered to your account after successful payment (requires transaction proof)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-white/40">•</span>
                  Unauthorized transactions (subject to investigation and verification)
                </li>
              </ul>
              <p className="text-white/50 text-sm mt-6">
                <strong className="text-white/70">Note:</strong> All exception requests must be made within 24 hours 
                of the transaction and are subject to our review process. We reserve the right to deny any request 
                that does not meet our criteria.
              </p>
            </div>
          </div>

          {/* Cancellation */}
          <div className="mb-16">
            <h2 className="text-3xl font-medium text-white mb-8">Cancellation Policy</h2>
            
            <div className="p-6 border border-white/10 bg-white/[0.02] rounded-lg">
              <p className="text-white/70 leading-relaxed">
                <strong className="text-white">Orders cannot be cancelled</strong> once payment is initiated. 
                Since credits are delivered instantly upon payment confirmation, there is no window for 
                cancellation. Please ensure you want to proceed before completing your purchase.
              </p>
            </div>
          </div>

          {/* Contact for Issues */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <Mail className="h-7 w-7 text-accent" />
              <h2 className="text-3xl font-medium text-white">Having Issues?</h2>
            </div>

            <div className="bg-white/[0.02] border border-white/10 p-8 rounded-lg">
              <p className="text-white/70 mb-6 leading-relaxed">
                If you experience any technical issues with your purchase or credits not being delivered, 
                please contact our support team immediately:
              </p>
              
              <div className="bg-[#111111] border border-white/10 p-6 rounded mb-6">
                <p className="text-white/40 text-xs font-mono tracking-widest mb-4">SUPPORT EMAIL</p>
                <a 
                  href="mailto:neopassbusiness@gmail.com" 
                  className="text-xl text-accent hover\:text-accent-light transition-colors"
                >
                  neopassbusiness@gmail.com
                </a>
              </div>

              <p className="text-white/70 mb-4">Please include:</p>
              <ul className="list-disc list-inside text-white/60 space-y-2 ml-4">
                <li>Your registered email address</li>
                <li>Transaction ID or payment reference</li>
                <li>Date and time of purchase</li>
                <li>Screenshot of payment confirmation (if available)</li>
                <li>Description of the issue</li>
              </ul>
            </div>
          </div>

          {/* Agreement Notice */}
          <div className="border border-white/20 bg-white/[0.02] p-8 rounded-lg">
            <h3 className="text-xl font-medium text-white mb-4">By Making a Purchase</h3>
            <p className="text-white/60 leading-relaxed">
              By completing a purchase on NeoPass Learning, you acknowledge that you have read, understood, 
              and agree to this Refund & Cancellation Policy. You accept that all sales are final and 
              non-refundable as stated above. If you do not agree with these terms, please do not proceed 
              with any purchase.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-6">
            Have questions before purchasing?
          </h2>
          <p className="text-white/50 mb-10 max-w-xl mx-auto">
            We're here to help you understand our service. Reach out before making a purchase.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="mailto:neopassbusiness@gmail.com?subject=Pre-Purchase Question"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-colors"
            >
              Contact Support
              <ChevronRight className="h-4 w-4" />
            </a>
            <Link 
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white font-medium hover:bg-white/5 transition-colors"
            >
              View Credit Packs
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer variant="full" activeLink="refunds" />
    </div>
  );
};

export default RefundPolicy;
